import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getScorecardTargets, getTeamScorecard } from '../../api/endpoints/ops';
import { historyQueryPolicy, staticQueryPolicy } from '../../api/queryPolicies';
import { ErrorState, LoadingState } from '../../components/ui/AsyncState';
import { KpiCard } from '../../components/ui/KpiCard';
import { SectionCard } from '../../components/ui/SectionCard';
import { OwnerChip } from '../ownership/OwnerChip';
import { useOpsUser } from '../ownership/opsUser';
import {
  addOneDay,
  durationAccent,
  fmtDuration,
  fmtRate,
  rateAccent,
} from './scorecardFormat';
import styles from '../analytics/AnalyticsView.module.css';
import { KpiIcon } from '../../components/ui/KpiIcon';

interface Props {
  fromDate: string;
  toDate: string;
}

export function OpsTeamScorecardTab({ fromDate, toDate }: Props) {
  const { t } = useTranslation();
  const { opsUser } = useOpsUser();

  const { data: targets } = useQuery({
    queryKey: ['admin', 'settings', 'scorecard-targets'],
    queryFn: getScorecardTargets,
    ...staticQueryPolicy,
  });

  const apiToDate = addOneDay(toDate);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'scorecard', 'team', fromDate, apiToDate],
    queryFn: () => getTeamScorecard({ from: fromDate, to: apiToDate }),
    ...historyQueryPolicy,
  });

  if (isLoading) return <LoadingState label={t('scorecard.opsTeamScorecardTab.loading')} />;
  if (isError || !data) {
    return <ErrorState title={t('scorecard.opsTeamScorecardTab.loadError')} onRetry={() => refetch()} />;
  }

  const { team, people } = data;
  const targetWeekly = targets?.weeklyFulfilledTarget ?? 10;
  const targetClaim = targets?.claimTargetMinutes ?? 15;
  const targetDriver = targets?.driverTargetMinutes ?? 120;
  const targetPod = (targets?.podCloseTargetHours ?? 24) * 60;

  return (
    <>
      <div className={styles.kpiRow} data-testid="team-kpis">
        <KpiCard
          label={t('scorecard.opsTeamScorecardTab.teamFulfilled')}
          value={team.fulfilled != null ? team.fulfilled : '—'}
          icon={<KpiIcon name="inventory" />}
        />
        <KpiCard
          label={t('scorecard.opsTeamScorecardTab.fulfilmentRate')}
          value={fmtRate(team.fulfilmentRate)}
          accent={rateAccent(team.fulfilmentRate)}
          icon={<KpiIcon name="target" />}
        />
        <KpiCard
          label={t('scorecard.opsTeamScorecardTab.timeToClaim')}
          value={fmtDuration(team.medianClaimMinutes)}
          accent={durationAccent(team.medianClaimMinutes, targetClaim)}
          icon={<KpiIcon name="timer" />}
        />
        <KpiCard
          label={t('scorecard.opsTeamScorecardTab.timeToDriver')}
          value={fmtDuration(team.medianDriverMinutes)}
          accent={durationAccent(team.medianDriverMinutes, targetDriver)}
          icon={<KpiIcon name="truck" />}
        />
        <KpiCard
          label={t('scorecard.opsTeamScorecardTab.onTimePickup')}
          value={fmtRate(team.onTimePickupRate)}
          accent={rateAccent(team.onTimePickupRate)}
          icon={<KpiIcon name="checkCircle" />}
        />
        <KpiCard
          label={t('scorecard.opsTeamScorecardTab.podClosure')}
          value={fmtDuration(team.medianPodCloseMinutes)}
          accent={durationAccent(team.medianPodCloseMinutes, targetPod)}
          icon={<KpiIcon name="document" />}
        />
        <KpiCard
          label={t('scorecard.opsTeamScorecardTab.openOrders')}
          value={team.openOrders != null ? team.openOrders : '—'}
          icon={<KpiIcon name="clipboard" />}
        />
      </div>

      <SectionCard title={t('scorecard.opsTeamScorecardTab.breakdownTitle')}>
        {people.length === 0 ? (
          <p className={styles.empty}>{t('scorecard.opsTeamScorecardTab.noMembers')}</p>
        ) : (
          <table className={styles.table} data-testid="ops-team-table">
            <thead>
              <tr>
                <th>{t('scorecard.opsTeamScorecardTab.person')}</th>
                <th>{t('scorecard.opsTeamScorecardTab.fulfilled')}</th>
                <th>{t('scorecard.opsTeamScorecardTab.fulfilmentRate')}</th>
                <th>{t('scorecard.opsTeamScorecardTab.timeToClaim')}</th>
                <th>{t('scorecard.opsTeamScorecardTab.timeToDriver')}</th>
                <th>{t('scorecard.opsTeamScorecardTab.onTimePickup')}</th>
                <th>{t('scorecard.opsTeamScorecardTab.podClosure')}</th>
                <th>{t('scorecard.opsTeamScorecardTab.openOrders')}</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => {
                const fulfilled = p.metrics.fulfilled;
                const fulfilledText =
                  fulfilled != null ? `${fulfilled} / ${targetWeekly}` : `— / ${targetWeekly}`;
                return (
                  <tr key={p.opsUser.id}>
                    <td>
                      <OwnerChip owner={p.opsUser} meId={opsUser?.id} />
                    </td>
                    <td>{fulfilledText}</td>
                    <td>{fmtRate(p.metrics.fulfilmentRate)}</td>
                    <td>{fmtDuration(p.metrics.medianClaimMinutes)}</td>
                    <td>{fmtDuration(p.metrics.medianDriverMinutes)}</td>
                    <td>{fmtRate(p.metrics.onTimePickupRate)}</td>
                    <td>{fmtDuration(p.metrics.medianPodCloseMinutes)}</td>
                    <td>{p.metrics.openOrders != null ? p.metrics.openOrders : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </SectionCard>
    </>
  );
}
